import ArrowLeft from '@mds/icons/ArrowLeft';
import { Box, Button, Stack, StackProps, SxProps, Theme, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { forwardRef, Fragment, isValidElement, ReactNode } from 'react';

export interface HeaderCallout {
  label: string;
  value: ReactNode;
}

interface HeaderBaseProps {
  /**
   * Page title — always rendered. Pass a string to use the default `h4` Typography variant,
   * or pass a `<Typography>` element to control the variant yourself.
   * @example "Account 001MRX"
   * @example <Typography variant="h5">Account 001MRX</Typography>
   */
  title: ReactNode;
  /** Avatar shown to the left of the title */
  avatar?: ReactNode;
  /** Status chip shown to the right of the title */
  chip?: ReactNode;
  /** Secondary line shown below the title row */
  email?: ReactNode;
  /** Secondary line shown below email */
  description?: ReactNode;
  /** Key–value pairs rendered as "Label: value · …" */
  callouts?: HeaderCallout[];
  /** Note value rendered as "Notes: {note}" */
  note?: ReactNode;
  /** Right-aligned slot in the title row */
  actions?: ReactNode;
  sx?: SxProps<Theme>;
}

interface HeaderWithBack extends HeaderBaseProps {
  /** When provided, a "Back to previous" button is rendered above the title. Mutually exclusive with `breadcrumbs`. */
  onBack: () => void;
  breadcrumbs?: never;
}

interface HeaderWithBreadcrumbs extends HeaderBaseProps {
  onBack?: never;
  /**
   * Breadcrumb navigation rendered above the title row. Pass a MUI `<Breadcrumbs>` element.
   * Mutually exclusive with `onBack`.
   */
  breadcrumbs: ReactNode;
}

interface HeaderWithoutNav extends HeaderBaseProps {
  onBack?: never;
  breadcrumbs?: never;
}

/**
 * Props for the `Header` component.
 *
 * Navigation above the title is either a back button (`onBack`) **or**
 * breadcrumbs (`breadcrumbs`) — never both at the same time.
 */
export type HeaderOwnProps = HeaderWithBack | HeaderWithBreadcrumbs | HeaderWithoutNav;

/**
 * Runtime doc-only prop shape. `onBack` and `breadcrumbs` are enforced as mutually
 * exclusive via the `HeaderOwnProps` union — do not use this interface directly.
 */
export interface HeaderDocProps extends HeaderBaseProps {
  /** When provided, a "Back to previous" button is rendered above the title. Mutually exclusive with `breadcrumbs`. */
  onBack?: () => void;
  /**
   * Breadcrumb navigation rendered above the title row. Pass a MUI `<Breadcrumbs>` element.
   * Mutually exclusive with `onBack`.
   */
  breadcrumbs?: ReactNode;
}

export type HeaderProps = HeaderOwnProps & Omit<StackProps, 'sx' | 'title' | 'children'>;

const Root = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(2),
}));

const TitleRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
});

const TitleLeft = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
});

const CalloutsRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 16,
});

const CalloutItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const ActionsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexShrink: 0,
}));

const BackButton = styled(Button)(({ theme }) => ({
  '--variant-textColor': theme.palette.text.primary,
}));

const getCalloutKey = (callout: HeaderCallout) => {
  if (typeof callout.value === 'string' || typeof callout.value === 'number') {
    return `${callout.label}-${callout.value}`;
  }

  if (isValidElement(callout.value) && callout.value.key != null) {
    return `${callout.label}-${callout.value.key}`;
  }

  return callout.label;
};

export const Header = forwardRef<HTMLDivElement, HeaderProps>(function Header(
  { title, avatar, chip, email, description, callouts, note, actions, onBack, breadcrumbs, ...rest },
  ref,
) {
  if (process.env.NODE_ENV !== 'production' && breadcrumbs && onBack) {
    console.warn('Header: `breadcrumbs` and `onBack` are mutually exclusive. `breadcrumbs` takes precedence.');
  }

  return (
    <Root {...rest} ref={ref}>
      {breadcrumbs ? (
        <Box>{breadcrumbs}</Box>
      ) : (
        onBack && (
          <Box>
            <BackButton variant="text" size="small" startIcon={<ArrowLeft />} onClick={onBack} sx={{ px: 1.5 }}>
              Back to previous
            </BackButton>
          </Box>
        )
      )}

      <TitleRow>
        <TitleLeft>
          {avatar}
          {typeof title === 'string' ? <Typography variant="h4">{title}</Typography> : title}
          {chip}
        </TitleLeft>
        {actions && <ActionsRow>{actions}</ActionsRow>}
      </TitleRow>

      {email && (
        <Typography variant="body1" color="textSecondary">
          {email}
        </Typography>
      )}

      {description && (
        <Typography variant="body1" color="textSecondary">
          {description}
        </Typography>
      )}

      {callouts && callouts.length > 0 && (
        <CalloutsRow>
          {callouts.map((callout, index) => (
            <Fragment key={getCalloutKey(callout)}>
              {index > 0 && (
                <Typography variant="label2" color="textSecondary">
                  ·
                </Typography>
              )}
              <CalloutItem>
                <Typography variant="label2" color="textSecondary">
                  {callout.label}:
                </Typography>
                <Typography variant="label2" color="textPrimary">
                  {callout.value}
                </Typography>
              </CalloutItem>
            </Fragment>
          ))}
        </CalloutsRow>
      )}

      {note && (
        <CalloutItem>
          <Typography variant="label2" color="textSecondary">
            Notes:
          </Typography>
          <Typography variant="label2" color="textPrimary">
            {note}
          </Typography>
        </CalloutItem>
      )}
    </Root>
  );
});
